import React, { FC, useMemo, useCallback, memo } from 'react';
import { useLazyQuery } from '@apollo/client';
import _map from 'lodash/map';
import { gqlRepresentationTypeEnum as REPRESENTATION_TYPE } from '@minecraft.graphql-types';
import ErrorMessage from '@minecraft.error-message';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import {
  SEARCH_REPRESENTATIVES,
  SEARCH_REPRESENTATIVES_BY_NAME,
} from '../../../FilterBasic/FilterBasicCollection/FilterBasicCollectionGraphQL';
import { FilterData } from '../../../shared/types';
import { getRepresentativeName } from '../../../shared/utils';
import { Label } from '../../../shared/styles';

type Props = FilterData & {
  searchByRoleSubmissionAndName?: boolean;
  roleId?: number | string;
};

const Representatives: FC<Props> = ({ name, label, searchByRoleSubmissionAndName, roleId }) => {
  const [getRepresentatives, { data, error, loading, fetchMore }] = useLazyQuery(
    searchByRoleSubmissionAndName ? SEARCH_REPRESENTATIVES_BY_NAME : SEARCH_REPRESENTATIVES
  );

  const representativesOptions = useMemo(() => {
    const representativesList = searchByRoleSubmissionAndName
      ? data?.organizationsByRoleSubmissionsAndName?.objects?.map((obj) => obj.organization)
      : data?.searchRepresentatives?.organizations;

    if (!representativesList) return [];

    return _map(representativesList, (representative) => ({
      value: representative.id,
      label: getRepresentativeName({
        divisionName: representative.name,
        companyName: representative?.parentOrganization?.name,
      }),
    }));
  }, [data]);

  const handleRepresentativesFetch = useCallback((searchValue: string, offset: number, limit: number): void => {
    getRepresentatives({
      variables: searchByRoleSubmissionAndName
        ? {
            roleId: +roleId,
            divisionOrAgencyName: searchValue || '',
            page: {
              pageNumber: offset,
              pageSize: limit,
            },
          }
        : {
            input: {
              companyName: searchValue,
              representativeTypeCodes: [REPRESENTATION_TYPE.AGENCY, REPRESENTATION_TYPE.MANAGEMENT],
            },
            page: {
              pageNumber: offset,
              pageSize: limit,
            },
          },
    });
  }, []);

  const handleRepresentativesFetchMore = useCallback(
    async (_, offset, limit) => {
      await fetchMore({
        variables: {
          page: {
            pageNumber: offset,
            pageSize: limit,
          },
        },
      });
    },
    [fetchMore]
  );

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <>
      <Label>{label}</Label>
      <DropdownWithChildren
        withFormik
        withSearch
        withAutocomplete
        withSelectionsResult
        isFullWidth
        isMultipleSelect
        isLoading={loading}
        initialOffset={1}
        initialLimit={20}
        name={name}
        optionsTotalCount={
          searchByRoleSubmissionAndName
            ? data?.organizationsByRoleSubmissionsAndName?.totalCount
            : data?.searchRepresentatives?.totalCount
        }
        options={representativesOptions}
        onOptionsFetch={handleRepresentativesFetch}
        onOptionsFetchMore={handleRepresentativesFetchMore}
        dataTestId="reps-filter-dd"
      />
    </>
  );
};

export default memo(Representatives);
